export const areFloatsEqual = (a: number, b: number, epsilon = Number.EPSILON) => {
  return Math.abs(a - b) <= epsilon
}

export const isFloatLessWithPrecision = (a: number, b: number, precision = 10) => {
  const factor = Math.pow(10, precision)
  return Math.round(a * factor) < Math.round(b * factor)
}

export const isFloatGreaterWithPrecision = (a: number, b: number, precision = 10) => {
  const factor = Math.pow(10, precision)
  return Math.round(a * factor) > Math.round(b * factor)
}

export const round = (a: number, precision = 10) => {
  const factor = Math.pow(10, precision)
  return Math.round(a * factor) / factor
}

export const meanBy = <T>(array: T[], iteratee: (value: T) => number) => {
  return (
    array.reduce((acc, value) => {
      return acc + iteratee(value)
    }, 0) / array.length
  )
}

export const sumBy = <T>(array: T[], iteratee: (value: T) => number) => {
  return array.reduce((acc, value) => {
    return acc + iteratee(value)
  }, 0)
}

/**
 * Sums values from an array using a provided iterator function, handling null values gracefully.
 *
 * If all iterator results are null, returns null. Otherwise, returns the sum of all non-null values,
 * treating null values as if they don't exist (they don't contribute to the sum and don't make the
 * result null).
 *
 * @template T - The type of items in the array
 * @param {T[]} items - The array of items to process
 * @param {(item: T) => number | null} iterator - Function that extracts a numeric value or null from each item
 * @returns {number | null} The sum of all non-null values, or null if all values are null
 *
 * @example
 * // Returns 6 (ignores the null)
 * nullableSumBy([{val: 1}, {val: 2}, {val: null}, {val: 3}], item => item.val)
 *
 * @example
 * // Returns null (all values are null)
 * nullableSumBy([{val: null}, {val: null}], item => item.val)
 *
 * @example
 * // Returns 0 (empty array)
 * nullableSumBy([], item => item.val)
 */
export const nullableSumBy = <T>(items: T[], iterator: (item: T) => number | null): number | null => {
  return items.reduce<number | null>((acc, item) => {
    const value = iterator(item)
    return value === null ? acc : (acc ?? 0) + value
  }, null)
}
